import { useOverlayPanel } from '@orgazone/ui-web';
import { useId, type ReactNode } from 'react';
import { Icon, type IconName } from './icons';

// Teinte de l'icône d'en-tête (reprise EXACTE de la maquette `modalIconSt`) : succès/erreur/avertissement/
// neutre/marque. Le pied et le corps restent pilotés par l'écran appelant (phases saisie→en cours→résultat).
type ModalTone = 'ok' | 'err' | 'warn' | 'ink' | 'teal';
const TONE: Record<ModalTone, [string, string]> = {
  ok: ['var(--ok-bg)', 'var(--ok-fg)'],
  err: ['var(--rose-bg)', 'var(--rose-fg)'],
  warn: ['var(--amber-bg)', 'var(--amber-fg)'],
  ink: ['var(--ink-100)', 'var(--ink-600)'],
  teal: ['var(--teal-100)', 'var(--teal-700)'],
};

// Modale unique de la console — forme reprise de la maquette (§ interactions transverses) : superposition +
// carte, en-tête (icône teintée + titre/sous-titre + croix carrée), corps scrollable, pied à boutons. `tone`
// colore l'icône (défaut « teal » = marque, pour ne pas changer les modales existantes).
// ⚠️ Le focus entre sur le premier contrôle — la croix de l'en-tête. Un appelant qui veut le poser dans un champ le fait
//    dans SON effet, sans `autoFocus` : un champ focalisé au montage serait pris pour le déclencheur, et le focus
//    reviendrait à la fermeture sur un champ qui n'existe plus au lieu du bouton qui a ouvert la modale.
// `tile` remplace l'icône teintée quand le sujet a sa propre couleur (un mapping prend celle de son type), et `accent`
// pose le liseré de cette couleur en haut de la carte ; `tabs` se pose au bas de l'en-tête, qui ne défile pas : on garde
// la section en vue en lisant le corps.
export function Modal({ title, sub, icon, tile, accent, tone = 'teal', onClose, wide, maxWidth, tabs, children, footer }: {
  title: string; sub?: ReactNode; icon?: IconName; tile?: ReactNode; accent?: string; tone?: ModalTone; onClose: () => void; wide?: boolean; maxWidth?: number; tabs?: ReactNode; children: ReactNode; footer?: ReactNode;
}) {
  // ⚠️ ÉCHAP FERME, ET LE FOCUS ENTRE PUIS REVIENT AU DÉCLENCHEUR : au seul pointeur, la modale ne se quittait pas au clavier.
  const { panelRef } = useOverlayPanel({ open: true, onClose });
  const titleId = useId();
  const width = maxWidth ?? (wide ? 620 : 470);
  const [ibg, ifg] = TONE[tone] ?? TONE.teal;
  return (
    <div onClick={onClose} style={{ position: 'fixed', inset: 0, zIndex: 80, background: 'rgba(12,14,16,.45)', display: 'grid', placeItems: 'center', padding: 20 }}>
      <div ref={panelRef} role="dialog" aria-modal="true" aria-labelledby={titleId} onClick={(e) => e.stopPropagation()} style={{ width, maxWidth: '100%', maxHeight: '90vh', display: 'flex', flexDirection: 'column', background: 'var(--paper)', borderRadius: 'var(--r-md)', boxShadow: 'var(--sh-modal)', overflow: 'hidden' }}>
        {accent && <div aria-hidden="true" style={{ height: 3, flexShrink: 0, background: `linear-gradient(90deg, ${accent}, color-mix(in oklch, ${accent} 45%, var(--paper)))` }} />}
        <div style={{ padding: tabs ? '16px 18px 0' : '16px 18px', borderBottom: '1px solid var(--ink-200)', flexShrink: 0 }}>
          <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12 }}>
            {tile ?? (icon && <div style={{ width: 32, height: 32, borderRadius: 'var(--r-sm)', background: ibg, color: ifg, display: 'grid', placeItems: 'center', flexShrink: 0 }}><Icon name={icon} size={17} /></div>)}
            <div style={{ flex: 1, minWidth: 0 }}>
              <div id={titleId} style={{ fontSize: 15.5, fontWeight: 700, letterSpacing: '-0.02em', color: 'var(--ink-900)' }}>{title}</div>
              {sub && <div style={{ fontSize: 'var(--t-body)', color: 'var(--ink-500)', lineHeight: 1.5, marginTop: 3 }}>{sub}</div>}
            </div>
            <button type="button" onClick={onClose} className="btn-icon" aria-label="Fermer"><Icon name="x" size={15} /></button>
          </div>
          {tabs && <div style={{ marginTop: 10 }}>{tabs}</div>}
        </div>
        <div style={{ padding: '16px 18px', overflowY: 'auto', flex: 1 }}>{children}</div>
        {footer && <div style={{ padding: '13px 18px', borderTop: '1px solid var(--ink-200)', display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 8, flexWrap: 'wrap', flexShrink: 0 }}>{footer}</div>}
      </div>
    </div>
  );
}
